// ===========================================================================
// Agregados de atribución (#1521) para la vista Fuentes: leads por canal de
// captación y por campaña (`attribution.utm_campaign`). Puros, sin React, para
// que la vista y sus pruebas compartan exactamente la misma cuenta.
// ===========================================================================
import type { Lead } from "./leadsService";
import { CHANNELS, channelForSource, resolveChannel, type ChannelDef } from "./leadSources";

export interface ChannelRow {
  def: ChannelDef;
  total: number;
  nuevos: number;
  convertidos: number;
  conversion: number; // 0..100
}

export interface CampaignRow {
  campaign: string;
  /** Canales distintos vistos en esa campaña (etiquetas legibles). */
  channels: string[];
  total: number;
  nuevos: number;
  convertidos: number;
  conversion: number; // 0..100
}

/**
 * Canal efectivo de un lead: el guardado o el de su atribución si es un canal
 * real; `other` (o nada) cuenta como vacío y cae al que implica la fuente,
 * igual que `channelForLead` en el backend.
 */
export function leadChannelKey(lead: Lead): ChannelDef["key"] {
  const stored = resolveChannel(lead.channel || lead.attribution?.channel).key;
  return stored !== "other" ? stored : channelForSource(lead.source);
}

const pct = (part: number, total: number) => (total > 0 ? (part / total) * 100 : 0);

/** Una fila por canal del catálogo, en su orden; «Otro» solo si tiene leads. */
export function aggregateByChannel(leads: Lead[]): ChannelRow[] {
  const counts = new Map<string, { total: number; nuevos: number; convertidos: number }>();
  for (const lead of leads) {
    const key = leadChannelKey(lead);
    const c = counts.get(key) ?? { total: 0, nuevos: 0, convertidos: 0 };
    c.total += 1;
    if (lead.status === "new") c.nuevos += 1;
    if (lead.status === "converted") c.convertidos += 1;
    counts.set(key, c);
  }
  return CHANNELS.filter((def) => def.key !== "other" || counts.has("other")).map((def) => {
    const c = counts.get(def.key) ?? { total: 0, nuevos: 0, convertidos: 0 };
    return { def, total: c.total, nuevos: c.nuevos, convertidos: c.convertidos, conversion: pct(c.convertidos, c.total) };
  });
}

/** Solo los leads con `attribution.utm_campaign`; de más a menos leads. */
export function aggregateByCampaign(leads: Lead[]): CampaignRow[] {
  const rows = new Map<string, { channels: Set<string>; total: number; nuevos: number; convertidos: number }>();
  for (const lead of leads) {
    const raw = lead.attribution?.utm_campaign;
    const campaign = typeof raw === "string" ? raw.trim() : "";
    if (!campaign) continue;
    const r = rows.get(campaign) ?? { channels: new Set<string>(), total: 0, nuevos: 0, convertidos: 0 };
    r.channels.add(resolveChannel(leadChannelKey(lead)).label);
    r.total += 1;
    if (lead.status === "new") r.nuevos += 1;
    if (lead.status === "converted") r.convertidos += 1;
    rows.set(campaign, r);
  }
  return Array.from(rows.entries())
    .map(([campaign, r]) => ({
      campaign,
      channels: Array.from(r.channels).sort((a, b) => a.localeCompare(b, "es")),
      total: r.total,
      nuevos: r.nuevos,
      convertidos: r.convertidos,
      conversion: pct(r.convertidos, r.total),
    }))
    .sort((a, b) => b.total - a.total || a.campaign.localeCompare(b.campaign, "es"));
}
